import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { OnlinePrediction, Refresh, Search } from '@mui/icons-material'
import { apiService } from '../../services/api'
import type { AdminPalworldOnlinePlayer } from '../../types'

const guessPlatform = (userId: string): string => {
  const id = String(userId ?? '')
  if (id.startsWith('steam_')) return 'Steam'
  if (id.startsWith('ps5_')) return 'PS5'
  if (id.startsWith('xbox_')) return 'Xbox'
  if (id.startsWith('gdk_')) return 'GDK'
  return '—'
}

const safeDateLabel = (isoLike: string | null | undefined): string => {
  const s = String(isoLike ?? '').trim()
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleString('pt-BR')
}

const PalworldOnlinePlayersPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [players, setPlayers] = useState<AdminPalworldOnlinePlayer[]>([])
  const [filter, setFilter] = useState('')
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)

  const loadOnline = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiService.listarPalworldOnlineAdmin()
      const list = Array.isArray(data) ? data : []
      list.sort((a, b) => String(b.joinedAt ?? '').localeCompare(String(a.joinedAt ?? '')))
      setPlayers(list)
      setLastUpdatedAt(new Date())
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar players online.')
      setPlayers([])
      setLastUpdatedAt(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOnline()
  }, [])

  const playersFiltered = useMemo(() => {
    const term = filter.trim().toLowerCase()
    if (!term) return players
    return players.filter((p) => {
      const name = String(p.playerName ?? '').toLowerCase()
      const id = String(p.userId ?? '').toLowerCase()
      return name.includes(term) || id.includes(term)
    })
  }, [filter, players])

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <OnlinePrediction />
              Players Online
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
              <Typography sx={{ color: 'text.secondary' }}>
                Lista atual de jogadores conectados no servidor.
              </Typography>
              <Chip
                size="small"
                color="info"
                label={`${players.length} online`}
                sx={{ fontWeight: 900 }}
              />
              {lastUpdatedAt && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Atualizado: ${lastUpdatedAt.toLocaleTimeString('pt-BR')}`}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={loadOnline}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Atualizar
            </Button>
          </Stack>
        </Box>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 3 } }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField
                size="small"
                label="Buscar"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Nome do player ou userId (steam_, ps5_, gdk_)"
                sx={{ flex: 1 }}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
              <Button
                variant="outlined"
                onClick={() => setFilter('')}
                disabled={!filter.trim()}
              >
                Limpar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell>Plataforma</TableCell>
                      <TableCell align="right">Entrou em</TableCell>
                      <TableCell align="right">Atualizado em</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {playersFiltered.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 900 }}>
                            {p.playerName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={guessPlatform(p.userId)} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {safeDateLabel(p.joinedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {safeDateLabel(p.updatedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}

                    {playersFiltered.length === 0 && !error && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Nenhum player online no momento.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  )
}

export default PalworldOnlinePlayersPage
